// S8 — the CLIENT MIRROR of the closed 19-item go-live checklist catalog
// (Module 02 §3.6), plus the pure helpers S8 renders with.
//
// Why a mirror exists at all: `GET /v1/onboarding/checklist` on a
// never-evaluated tenant returns `items: []` with `score: 0`, and §4 S8 requires
// that page to render "the 19 catalog rows as `pending` placeholders with an
// explanatory line and a 'Run evaluation' CTA — never an empty page and never an
// implied all-clear". The client therefore needs the item set, its categories,
// weights and blocking flags to draw those placeholders. The server's
// `checklist.catalog.ts` remains the ONLY authority: whenever the API sends a
// row, the row wins — this table is used only for keys the response does not
// carry, and its shape is asserted against the response in the unit tests.
//
// The catalog is closed: 19 items, Σ weight 119, 5 blocking. Adding one here
// without editing spec §3.6 (and the server catalog) in the same PR is exactly
// the drift this file's tests exist to catch.

import type { components } from "@assessly/api-client";

type S = components["schemas"];

export type ChecklistItem = S["OnboardingChecklistItem"];
export type ChecklistCategory = ChecklistItem["category"];
export type ChecklistStatus = ChecklistItem["status"];

/**
 * Where a user goes to FIX an item (§4 S8 "sample links"). Without it the
 * checklist names what is wrong but never where to act, so a manager blocked on
 * `catalogue.boms_cover_products` and `plant.work_centers_min` has to work out
 * for themselves which step each belongs to. `null` = nothing the user can do
 * here (the owning module is unwired, or it is a manual attestation).
 */
export type ChecklistFixRoute =
  | "/onboarding/vertical"
  | "/onboarding/quality"
  | "/onboarding/catalogue"
  | "/onboarding/plant"
  | "/onboarding/opening"
  | null;

export interface CatalogEntry {
  readonly key: string;
  readonly category: ChecklistCategory;
  readonly kind: ChecklistItem["kind"];
  readonly blocking: boolean;
  readonly weight: number;
  readonly fixRoute: ChecklistFixRoute;
}

/** The §3.6 item table, in spec order. */
export const CHECKLIST_CATALOG: readonly CatalogEntry[] = [
  {
    key: "vertical.pack_installed",
    category: "vertical",
    kind: "auto",
    blocking: true,
    weight: 10,
    fixRoute: "/onboarding/vertical",
  },
  {
    key: "quality.checks_reviewed",
    category: "quality",
    kind: "auto",
    blocking: true,
    weight: 8,
    fixRoute: "/onboarding/quality",
  },
  {
    key: "catalogue.products_min",
    category: "catalogue",
    kind: "auto",
    blocking: true,
    weight: 10,
    fixRoute: "/onboarding/catalogue",
  },
  {
    key: "catalogue.boms_cover_products",
    category: "catalogue",
    kind: "auto",
    blocking: false,
    weight: 8,
    fixRoute: "/onboarding/catalogue",
  },
  {
    key: "catalogue.routings_cover_products",
    category: "catalogue",
    kind: "auto",
    blocking: false,
    weight: 8,
    fixRoute: "/onboarding/catalogue",
  },
  {
    key: "catalogue.routings_bound",
    category: "catalogue",
    kind: "auto",
    blocking: true,
    weight: 8,
    fixRoute: "/onboarding/plant",
  },
  {
    key: "plant.facility_min",
    category: "plant",
    kind: "auto",
    blocking: true,
    weight: 10,
    fixRoute: "/onboarding/plant",
  },
  {
    key: "plant.departments_min",
    category: "plant",
    kind: "auto",
    blocking: false,
    weight: 5,
    fixRoute: "/onboarding/plant",
  },
  {
    key: "plant.work_centers_min",
    category: "plant",
    kind: "auto",
    blocking: true,
    weight: 8,
    fixRoute: "/onboarding/plant",
  },
  {
    key: "plant.machines_calibration",
    category: "plant",
    kind: "auto",
    blocking: false,
    weight: 4,
    fixRoute: "/onboarding/plant",
  },
  {
    key: "people.admin_per_facility",
    category: "people",
    kind: "auto",
    blocking: false,
    weight: 6,
    fixRoute: "/onboarding/plant",
  },
  {
    key: "people.floor_workers_min",
    category: "people",
    kind: "auto",
    blocking: false,
    weight: 5,
    fixRoute: "/onboarding/plant",
  },
  {
    key: "people.invites_ready",
    category: "people",
    kind: "auto",
    blocking: false,
    weight: 3,
    fixRoute: "/onboarding/plant",
  },
  {
    key: "opening.stock_entered",
    category: "opening",
    kind: "auto",
    blocking: false,
    weight: 7,
    fixRoute: "/onboarding/opening",
  },
  {
    key: "opening.stock_valued",
    category: "opening",
    kind: "auto",
    blocking: false,
    weight: 5,
    fixRoute: "/onboarding/opening",
  },
  {
    key: "opening.open_orders_entered",
    category: "opening",
    kind: "auto",
    blocking: false,
    weight: 4,
    fixRoute: "/onboarding/opening",
  },
  {
    key: "opening.customers_entered",
    category: "opening",
    kind: "auto",
    blocking: false,
    weight: 5,
    fixRoute: "/onboarding/opening",
  },
  {
    key: "channels.qr_kit_printed",
    category: "channels",
    kind: "auto",
    blocking: false,
    weight: 5,
    fixRoute: null,
  },
  {
    key: "channels.floor_pwa_tested",
    category: "channels",
    kind: "manual",
    blocking: false,
    weight: 5,
    fixRoute: null,
  },
  {
    key: "channels.notification_channel",
    category: "channels",
    kind: "manual",
    blocking: false,
    weight: 3,
    fixRoute: null,
  },
];

/** Σ weight over the whole catalog — the §3.6 score denominator. */
export const CHECKLIST_TOTAL_WEIGHT = CHECKLIST_CATALOG.reduce((n, i) => n + i.weight, 0);

/** Category sections, in the order S8 renders them. */
export const CHECKLIST_CATEGORIES: readonly ChecklistCategory[] = [
  "vertical",
  "quality", // D-214
  "catalogue",
  "plant",
  "people",
  "opening",
  "channels",
];

/**
 * The rows S8 renders: every server row, in catalog order, with a `pending`
 * placeholder standing in for any catalog key the response does not carry (an
 * unevaluated checklist, or a row the evaluator has not written yet). Server
 * rows always win. A row whose key left the catalog is still rendered — the
 * server pruning it is the server's business, and hiding it would be a lie.
 */
export function checklistRows(items: readonly ChecklistItem[]): ChecklistItem[] {
  const byKey = new Map(items.map((i) => [i.item_key, i]));
  const rows: ChecklistItem[] = CHECKLIST_CATALOG.map((entry) => {
    const server = byKey.get(entry.key);
    if (server) return server;
    return {
      item_key: entry.key,
      category: entry.category,
      kind: entry.kind,
      blocking: entry.blocking,
      weight: entry.weight,
      status: "pending",
      detail: {},
      waived_by_user_id: null,
      waived_reason: null,
      evaluated_at: null,
    };
  });
  const known = new Set(CHECKLIST_CATALOG.map((e) => e.key));
  return [...rows, ...items.filter((i) => !known.has(i.item_key))];
}

/** True when the tenant has never been evaluated (§4 S8 empty state). */
export function isNeverEvaluated(items: readonly ChecklistItem[]): boolean {
  return items.length === 0;
}

export interface CategorySection {
  readonly category: ChecklistCategory;
  readonly items: readonly ChecklistItem[];
  /** Weight met (passed ∪ waived) / weight of this section — the §3.6 formula. */
  readonly earned: number;
  readonly total: number;
}

/** Rows grouped into the six §3.6 categories, in render order. */
export function groupByCategory(rows: readonly ChecklistItem[]): CategorySection[] {
  const known = new Set<string>(CHECKLIST_CATEGORIES);
  const categories: ChecklistCategory[] = [
    ...CHECKLIST_CATEGORIES,
    ...rows.map((r) => r.category).filter((c) => !known.has(c)),
  ];
  const seen = new Set<ChecklistCategory>();
  const sections: CategorySection[] = [];
  for (const category of categories) {
    if (seen.has(category)) continue;
    seen.add(category);
    const items = rows.filter((r) => r.category === category);
    if (items.length === 0) continue;
    sections.push({
      category,
      items,
      earned: items.filter(isMet).reduce((n, i) => n + i.weight, 0),
      total: items.reduce((n, i) => n + i.weight, 0),
    });
  }
  return sections;
}

/** §3.6: `passed ∪ waived` counts toward the score; pending and failed do not. */
export function isMet(item: ChecklistItem): boolean {
  return item.status === "passed" || item.status === "waived";
}

/**
 * A `pending` item whose owning module is not wired (`detail.checkable === false`
 * with a `blocked_on_module_key`). Nobody can fix it, so S8 must not offer "go
 * do this" — only the module it waits on, and a waiver.
 */
export function blockedOnModuleKey(item: ChecklistItem): string | undefined {
  const value = item.detail["blocked_on_module_key"];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/** True when the server says this item cannot be checked in this deployment. */
export function isCheckable(item: ChecklistItem): boolean {
  return item.detail["checkable"] !== false;
}

/** §3.6 vacuous satisfaction — the rule passed over an empty set, and says so. */
export function isVacuous(item: ChecklistItem): boolean {
  return item.detail["vacuous"] === true;
}

/** Evidence members of `detail`, minus the machine-readable plumbing S8 renders
 * separately (the rule key, the blocked-on-module trio, the flags). */
const DETAIL_META = new Set([
  "rule",
  "rule_key",
  "reason",
  "reason_key",
  "reason_params",
  "blocked_on_module",
  "blocked_on_module_key",
  "checkable",
  "vacuous",
]);

export interface EvidenceEntry {
  readonly field: string;
  readonly value: string;
}

/** Flatten an item's evidence into printable field/value pairs, in a stable
 * order, so the detail expander shows the counts the verdict rested on. */
export function evidenceOf(item: ChecklistItem): EvidenceEntry[] {
  return Object.entries(item.detail)
    .filter(([field]) => !DETAIL_META.has(field))
    .map(([field, value]) => ({ field, value: printable(value) }))
    .filter((entry) => entry.value.length > 0);
}

function printable(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value))
    return value
      .map(printable)
      .filter((v) => v.length > 0)
      .join(", ");
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(([k, v]) => `${k}: ${printable(v)}`)
      .join(", ");
  }
  return "";
}

/** i18n key for the §3.6 rule prose of an item (`detail.rule_key` mirrors it). */
export function ruleKeyOf(item: ChecklistItem): string {
  const key = item.detail["rule_key"];
  return typeof key === "string" && key.length > 0
    ? key
    : `onboarding.checklist.rule.${item.item_key}`;
}

/**
 * i18n key for a module key the API sends (`module.06_inventory` →
 * `onboarding.checklist.module.06_inventory`). The API's own dotted key cannot
 * be used directly: i18next would read the dot as a namespace separator.
 */
/**
 * Where to go and fix this item (§4 S8). `null` when there is nothing the user
 * can do from the wizard — a `pending` item whose owning module is unwired is
 * never given a "Fix this" link, because there would be nothing behind it
 * (D-033).
 */
export function fixRouteOf(item: ChecklistItem): ChecklistFixRoute {
  if (!isCheckable(item)) return null;
  return CHECKLIST_CATALOG.find((e) => e.key === item.item_key)?.fixRoute ?? null;
}

export function moduleLabelKey(moduleKey: string): string {
  return `onboarding.checklist.module.${moduleKey.replace(/^module\./, "")}`;
}
